import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Brain, 
  Database, 
  AlertCircle, 
  CheckCircle, 
  Loader2, 
  Users, 
  Zap,
  Plus,
  Settings
} from 'lucide-react';
import ContextAwareChat from '../components/ContextAwareChat';
import IntelligentChatAgent from '../components/IntelligentChatAgent';

interface HealthStatus {
  status: string;
  neo4j: string;
  timestamp: string;
}

interface Project {
  projectId: string;
  projectName?: string;
  creationDate?: string;
  interactionCount: number;
  lastActivity?: string;
}

interface User {
  id: number;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  createdAt: string;
}

interface UserStats {
  interactionCount: number;
  interests: string[];
  preferences: string[];
  firstInteraction?: string;
  lastInteraction?: string;
}

const KnowledgeGraphDemo: React.FC = () => {
  // Helper function to convert Neo4j integers to regular JavaScript numbers
  const convertNeo4jInteger = (value: any): number => {
    if (typeof value === 'object' && value?.low !== undefined) {
      return value.low;
    }
    return typeof value === 'number' ? value : 0;
  };

  // Admin mode state
  const [isAdminMode, setIsAdminMode] = useState(true);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  
  // Original demo state (for fallback)
  const [userId, setUserId] = useState('demo-user-123');
  const [currentProjectId, setCurrentProjectId] = useState('demo-project-1');
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [isHealthLoading, setIsHealthLoading] = useState(false);
  const [userProjects, setUserProjects] = useState<Project[]>([]);
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sample data for demo
  const sampleProjects = [
    { projectId: 'demo-project-1', projectName: 'AI Chatbot Development', interactionCount: 0 },
    { projectId: 'demo-project-2', projectName: 'Data Visualization Dashboard', interactionCount: 0 },
    { projectId: 'demo-project-3', projectName: 'E-commerce Platform', interactionCount: 0 }
  ];

  const sampleInteractions = [
    {
      projectId: 'demo-project-1',
      text: 'I need help implementing a context-aware chatbot using machine learning and TypeScript. I prefer detailed responses with code examples.',
    },
    {
      projectId: 'demo-project-1', 
      text: 'How can I optimize the performance of my React components for better user experience?',
    },
    {
      projectId: 'demo-project-2',
      text: 'I want to create interactive data visualization charts. I like using modern design patterns.',
    },
    {
      projectId: 'demo-project-2',
      text: 'What are the best practices for handling large datasets in frontend applications?',
    },
    {
      projectId: 'demo-project-3',
      text: 'I need to implement a secure payment system with API development and database design.',
    },
    {
      projectId: 'demo-project-3',
      text: 'How do I handle user authentication and session management in my e-commerce platform?',
    }
  ];

  useEffect(() => {
    checkHealth();
    if (isAdminMode) {
      fetchAllUsers();
    }
  }, [isAdminMode]);

  const checkHealth = async () => {
    setIsHealthLoading(true);
    try {
      const response = await fetch('/api/knowledge-graph/health');
      const data = await response.json();
      setHealthStatus(data);
    } catch (error) {
      console.error('Health check failed:', error);
      setError('Failed to connect to Knowledge Graph service');
    } finally {
      setIsHealthLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await fetch('/api/admin/users', {
        credentials: 'include'
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          setIsAdminMode(false);
          return;
        }
        throw new Error('Failed to fetch users');
      }
      
      const users = await response.json();
      setAllUsers(users);
      
      // Auto-select first user if any exist
      if (users.length > 0) {
        handleUserSelect(users[0]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setIsAdminMode(false); // Fall back to demo mode
    }
  };

  const handleUserSelect = async (user: User) => {
    setSelectedUser(user);
    setError(null);
    
    try {
      // Fetch user statistics from knowledge graph
      const statsResponse = await fetch('/api/knowledge-graph/stats', {
        headers: { 'X-User-ID': user.id.toString() }
      });
      
      if (statsResponse.ok) {
        const stats = await statsResponse.json();
        // Convert Neo4j integers to regular numbers
        const processedStats = {
          ...stats.data,
          interactionCount: convertNeo4jInteger(stats.data.interactionCount),
          interests: stats.data.interests || [],
          preferences: stats.data.preferences || []
        };
        setUserStats(processedStats);
      }
      
      // Fetch user projects
      const projectsResponse = await fetch('/api/knowledge-graph/projects', {
        headers: { 'X-User-ID': user.id.toString() }
      });
      
      if (projectsResponse.ok) {
        const projectsData = await projectsResponse.json();
        // Convert Neo4j integers to regular numbers in project data
        const processedProjects = (projectsData.data || []).map((project: any) => ({
          ...project,
          interactionCount: convertNeo4jInteger(project.interactionCount)
        }));
        setUserProjects(processedProjects);
        
        // Set current project to first project if available
        if (processedProjects.length > 0) {
          setCurrentProjectId(processedProjects[0].projectId);
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('Failed to fetch user data');
    }
  };

  const initializeDemoData = async () => {
    setIsInitializing(true);
    setError(null);
    
    try {
      // Initialize database indexes
      await fetch('/api/knowledge-graph/admin/initialize', {
        method: 'POST',
      });

      // Create demo projects and interactions
      for (const project of sampleProjects) {
        // Update project metadata
        await fetch(`/api/knowledge-graph/projects/${project.projectId}`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'X-User-ID': userId
          },
          body: JSON.stringify({
            projectName: project.projectName,
            creationDate: new Date().toISOString()
          }),
        });
      }

      // Ingest sample interactions
      await fetch('/api/knowledge-graph/interactions/batch', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-User-ID': userId
        },
        body: JSON.stringify({
          interactions: sampleInteractions.map(interaction => ({
            ...interaction,
            timestamp: new Date().toISOString()
          }))
        }),
      });

      // Refresh projects list
      await fetchUserProjects();
      
      setError(null);
    } catch (error) {
      console.error('Demo initialization failed:', error);
      setError('Failed to initialize demo data. Please check if Neo4j is running.');
    } finally {
      setIsInitializing(false);
    }
  };

  const fetchUserProjects = async () => {
    try {
      const userIdToUse = isAdminMode && selectedUser ? selectedUser.id.toString() : userId;
      
      const response = await fetch('/api/knowledge-graph/projects', {
        method: 'GET',
        headers: { 
          'X-User-ID': userIdToUse
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Convert Neo4j integers to regular numbers in project data
        const processedProjects = (data.data || []).map((project: any) => ({
          ...project,
          interactionCount: convertNeo4jInteger(project.interactionCount)
        }));
        setUserProjects(processedProjects);
      }
    } catch (error) {
      console.error('Error fetching user projects:', error);
    }
  };

  const isHealthy = healthStatus?.status === 'healthy' && healthStatus?.neo4j === 'connected';

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold flex items-center justify-center gap-2">
          <Brain className="h-8 w-8" />
          Knowledge Graph Admin Console
        </h1>
        <p className="text-muted-foreground">
          {isAdminMode 
            ? 'Analyze user interactions, interests, and conversation patterns across the knowledge graph'
            : 'Experience intelligent chat with automatic context switching and project-aware responses'
          }
        </p>
      </div>

      {/* Health Status */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Database className="h-4 w-4" />
            System Status
            <Button 
              variant="outline" 
              size="sm" 
              onClick={checkHealth}
              disabled={isHealthLoading}
              className="ml-auto"
            >
              {isHealthLoading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                'Refresh'
              )}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isHealthLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Checking system health...
            </div>
          ) : healthStatus ? (
            <div className="flex items-center gap-2">
              {isHealthy ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-500" />
              )}
              <span className="text-sm">
                Knowledge Graph: {healthStatus.neo4j} | 
                API: {healthStatus.status} | 
                Last checked: {new Date(healthStatus.timestamp).toLocaleTimeString()}
              </span>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              Unable to check system status
            </div>
          )}
        </CardContent>
      </Card>

      {/* Demo Setup */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Settings className="h-4 w-4" />
            {isAdminMode ? 'Admin Interface' : 'Demo Configuration'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Mode Toggle */}
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Interface Mode</Label>
            <div className="flex items-center gap-2">
              <Button
                variant={!isAdminMode ? "default" : "outline"}
                size="sm"
                onClick={() => setIsAdminMode(false)}
              >
                Demo Mode
              </Button>
              <Button
                variant={isAdminMode ? "default" : "outline"}
                size="sm"
                onClick={() => setIsAdminMode(true)}
              >
                Admin Mode
              </Button>
            </div>
          </div>

          {isAdminMode ? (
            /* Admin Mode Interface */
            <>
              {/* User Selection */}
              <div>
                <Label htmlFor="userSelect">Select User to Analyze</Label>
                <div className="flex items-center gap-2 mt-1">
                  <select
                    id="userSelect"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    value={selectedUser?.id || ''}
                    onChange={(e) => {
                      const user = allUsers.find(u => u.id === parseInt(e.target.value));
                      if (user) handleUserSelect(user);
                    }}
                  >
                    <option value="">Select a user...</option>
                    {allUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.displayName} ({user.email}) - {user.role}
                      </option>
                    ))}
                  </select>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={fetchAllUsers}
                    disabled={!isHealthy}
                  >
                    Refresh
                  </Button>
                </div>
              </div>

              {/* User Stats Display */}
              {selectedUser && userStats && (
                <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 rounded-lg">
                  <div>
                    <Label className="text-xs text-muted-foreground">Total Interactions</Label>
                    <div className="text-lg font-semibold">{userStats.interactionCount}</div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Knowledge Areas</Label>
                    <div className="text-lg font-semibold">{userStats.interests.length}</div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Preferences</Label>
                    <div className="text-lg font-semibold">{userStats.preferences.length}</div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Projects</Label>
                    <div className="text-lg font-semibold">{userProjects.length}</div>
                  </div>
                </div>
              )}

              {/* Recent Interests */}
              {selectedUser && userStats && userStats.interests.length > 0 && (
                <div>
                  <Label className="text-xs font-medium text-muted-foreground mb-2 block">
                    Recent Interests:
                  </Label>
                  <div className="flex flex-wrap gap-1">
                    {userStats.interests.slice(0, 10).map((interest, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {interest}
                      </Badge>
                    ))}
                    {userStats.interests.length > 10 && (
                      <Badge variant="outline" className="text-xs">
                        +{userStats.interests.length - 10} more
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Demo Mode Interface */
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="userId">User ID</Label>
                  <Input
                    id="userId"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    placeholder="Enter user ID"
                  />
                </div>
                <div>
                  <Label htmlFor="projectId">Current Project ID</Label>
                  <Input
                    id="projectId"
                    value={currentProjectId}
                    onChange={(e) => setCurrentProjectId(e.target.value)}
                    placeholder="Enter project ID"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  {userProjects.length > 0 ? (
                    `${userProjects.length} projects available`
                  ) : (
                    'No projects found - initialize demo data to get started'
                  )}
                </div>
                <Button 
                  onClick={initializeDemoData}
                  disabled={isInitializing || !isHealthy}
                  size="sm"
                >
                  {isInitializing ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      Initializing...
                    </>
                  ) : (
                    <>
                      <Plus className="h-3 w-3 mr-1" />
                      Initialize Demo Data
                    </>
                  )}
                </Button>
              </div>
            </>
          )}

          {userProjects.length > 0 && (
            <div>
              <Label className="text-xs font-medium text-muted-foreground mb-2 block">
                Available Projects:
              </Label>
              <div className="flex flex-wrap gap-1">
                {userProjects.map((project) => (
                  <Badge 
                    key={project.projectId} 
                    variant={project.projectId === currentProjectId ? "default" : "secondary"}
                    className="text-xs cursor-pointer"
                    onClick={() => setCurrentProjectId(project.projectId)}
                  >
                    {project.projectName || project.projectId}
                    <span className="ml-1 opacity-70">({project.interactionCount})</span>
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Chat Interfaces */}
      {isHealthy && (isAdminMode ? selectedUser : true) && userProjects.length > 0 ? (
        <Tabs defaultValue="intelligent" className="space-y-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="simple" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Simple Context Chat
            </TabsTrigger>
            <TabsTrigger value="intelligent" className="flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Intelligent Agent
            </TabsTrigger>
          </TabsList>

          <TabsContent value="simple" className="space-y-4">
            <div className="text-center space-y-2 mb-4">
              <h3 className="text-lg font-semibold">Simple Context-Aware Chat</h3>
              <p className="text-sm text-muted-foreground">
                Basic chat interface that fetches context for the current project
                {isAdminMode && selectedUser && (
                  <span className="block mt-1 text-xs text-blue-600">
                    Viewing data for: {selectedUser.displayName} ({selectedUser.email})
                  </span>
                )}
              </p>
            </div>
            <ContextAwareChat
              userId={isAdminMode && selectedUser ? selectedUser.id.toString() : userId}
              currentProjectId={currentProjectId}
              projectName={userProjects.find(p => p.projectId === currentProjectId)?.projectName}
            />
          </TabsContent>

          <TabsContent value="intelligent" className="space-y-4">
            <div className="text-center space-y-2 mb-4">
              <h3 className="text-lg font-semibold">Intelligent Context-Switching Agent</h3>
              <p className="text-sm text-muted-foreground">
                Advanced AI agent that automatically detects when to switch project contexts
                {isAdminMode && selectedUser && (
                  <span className="block mt-1 text-xs text-blue-600">
                    Viewing data for: {selectedUser.displayName} ({selectedUser.email})
                  </span>
                )}
              </p>
            </div>
            <IntelligentChatAgent
              userId={isAdminMode && selectedUser ? selectedUser.id.toString() : userId}
              initialProjectId={currentProjectId}
              onProjectSwitch={setCurrentProjectId}
            />
          </TabsContent>
        </Tabs>
      ) : (
        <Card>
          <CardContent className="py-8 text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto" />
            <div>
              <h3 className="font-semibold">
                {isAdminMode ? 'Select a User to Analyze' : 'Ready to Get Started?'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {!isHealthy 
                  ? 'Please ensure Neo4j is running and accessible, then check system status.'
                  : isAdminMode 
                    ? 'Choose a user from the dropdown above to view their knowledge graph data and conversation insights.'
                    : 'Initialize demo data to start exploring the Context-Aware Knowledge Graph.'
                }
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Demo Instructions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">How to Use This Demo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <strong>1. Initialize Demo Data:</strong> Click the "Initialize Demo Data" button to create sample projects and interactions.
          </div>
          <div>
            <strong>2. Try Simple Chat:</strong> Ask questions about the current project to see context-aware responses.
          </div>
          <div>
            <strong>3. Test Intelligent Agent:</strong> Try mentioning other projects in your queries (e.g., "How does this compare to the Data Visualization Dashboard?") to see automatic context switching.
          </div>
          <div>
            <strong>4. Example Queries:</strong>
            <ul className="list-disc list-inside ml-4 space-y-1 text-muted-foreground">
              <li>"How can I improve the performance of my React components?"</li>
              <li>"Compare this to the E-commerce Platform approach"</li>
              <li>"Switch to Data Visualization Dashboard project"</li>
              <li>"I prefer detailed explanations with code examples"</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default KnowledgeGraphDemo; 